/**
 * SlabAnalysisPage — análisis de losas (1D y 2D).
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Layers, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { SaveDesignDialog } from '@/features/designs/SaveDesignDialog';
import { useDesign } from '@/features/designs/useDesigns';
import {
  Card,
  Divider,
  ForceItem,
  NumberField,
  Row,
  RowKV,
  Section,
  SelectField,
  WarningsCard,
} from '@/features/analysis/shared/primitives';
import { useSlabAnalysisStore } from '@/features/analysis/slabs/store';
import { useSlabAnalysisQuery } from '@/features/analysis/slabs/useSlabAnalysis';
import {
  SLAB_TYPE_LABELS,
  type EdgeCondition,
  type SlabAnalysisRequest,
  type SlabAnalysisType,
} from '@/features/analysis/slabs/types';
import {
  SUPPORT_LABELS,
  USE_LABELS,
  type LiveLoadUse,
  type SupportType,
} from '@/features/analysis/beams/types';

export function SlabAnalysisPage() {
  const request = useSlabAnalysisStore((s) => s.request);
  const setType = useSlabAnalysisStore((s) => s.setType);
  const setGeometry = useSlabAnalysisStore((s) => s.setGeometry);
  const setAutoLoads = useSlabAnalysisStore((s) => s.setAutoLoads);
  const setOneWay = useSlabAnalysisStore((s) => s.setOneWay);
  const setTwoWayEdges = useSlabAnalysisStore((s) => s.setTwoWayEdges);
  const loadRequest = useSlabAnalysisStore((s) => s.loadRequest);

  const params = useParams<{ id?: string }>();
  const designId =
    params.id && params.id !== 'new' && /^\d+$/.test(params.id) ? Number(params.id) : null;
  const designQuery = useDesign(designId);
  useEffect(() => {
    if (designQuery.data) {
      loadRequest(designQuery.data.payload as unknown as SlabAnalysisRequest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designQuery.data?.id]);

  const { data, isFetching } = useSlabAnalysisQuery(request);
  const navigate = useNavigate();
  const [saveOpen, setSaveOpen] = useState(false);

  const isTwoWay = request.geometry.type.endsWith('_2d');

  const onDesign = () => {
    if (!data) return;
    navigate('/app/slabs/new', {
      state: {
        prefill: {
          source: 'analysis',
          analysisId: designId,
          section: {
            type: data.design_prefill.type,
            L_span: data.design_prefill.L_span_cm,
            h_total: data.design_prefill.h_total_cm,
            bw: data.design_prefill.bw_cm,
            s: data.design_prefill.s_rib_cm,
            t_slab: data.design_prefill.t_slab_cm,
            continuity: data.design_prefill.continuity,
          },
          materials: { fc: request.fc, fy: request.fy, cover: request.cover_cm, TMA: 1.27 },
          loads: { WD: data.design_prefill.WD_kgf_m2, WL: data.design_prefill.WL_kgf_m2 },
        },
      },
    });
  };

  return (
    <div className="flex h-full flex-col">
      <header
        className="flex h-14 shrink-0 items-center justify-between border-b px-6"
        style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
      >
        <div>
          <div
            className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em]"
            style={{ color: 'var(--text-muted)' }}
          >
            <Layers size={12} strokeWidth={1.8} /> Análisis E.020 + ACI 318-19 / Method 3
          </div>
          <div className="text-sm font-semibold leading-none" style={{ color: 'var(--text-primary)' }}>
            Análisis de losas
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setSaveOpen(true)}>
            <Save size={14} strokeWidth={1.8} /> {designId ? 'Actualizar' : 'Guardar'}
          </Button>
          <Button variant="primary" size="md" onClick={onDesign} disabled={!data || isFetching}>
            Diseñar losa <ArrowRight size={14} strokeWidth={2} />
          </Button>
        </div>
      </header>

      <SaveDesignDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        module="analysis_losas"
        payload={request as unknown as Record<string, unknown>}
        existing={designQuery.data ?? null}
      />

      <div className="grid flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[340px_minmax(0,1fr)]">
        {/* INPUTS */}
        <div
          className="overflow-y-auto border-r p-5 scrollbar-thin"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
        >
          <Section title="Geometría">
            <SelectField
              label="Tipo de losa"
              value={request.geometry.type}
              options={SLAB_TYPE_LABELS as Record<string, string>}
              onChange={(v) => setType(v as SlabAnalysisType)}
            />
            <Row>
              <NumberField
                label="Lx (m)"
                value={request.geometry.Lx_m}
                onChange={(v) => setGeometry({ Lx_m: v })}
                step={0.1}
              />
              {isTwoWay && (
                <NumberField
                  label="Ly (m)"
                  value={request.geometry.Ly_m}
                  onChange={(v) => setGeometry({ Ly_m: v })}
                  step={0.1}
                />
              )}
            </Row>
            <NumberField
              label="Espesor (cm)"
              value={request.geometry.thickness_cm}
              onChange={(v) => setGeometry({ thickness_cm: v })}
            />
            {request.geometry.type.startsWith('aligerada') && (
              <Row>
                <NumberField
                  label="bw vigueta (cm)"
                  value={request.geometry.bw_cm}
                  onChange={(v) => setGeometry({ bw_cm: v })}
                />
                <NumberField
                  label="s c-c (cm)"
                  value={request.geometry.rib_spacing_cm}
                  onChange={(v) => setGeometry({ rib_spacing_cm: v })}
                />
              </Row>
            )}
          </Section>

          <Section title="Continuidad">
            {!isTwoWay && (
              <SelectField
                label="Apoyo (1D)"
                value={request.one_way.support_type}
                options={SUPPORT_LABELS as Record<string, string>}
                onChange={(v) => setOneWay({ support_type: v as SupportType })}
              />
            )}
            {isTwoWay && (
              <>
                <SelectField
                  label="Borde inferior"
                  value={request.two_way_edges.bottom}
                  options={{ continuo: 'Continuo', simple: 'Simple' }}
                  onChange={(v) => setTwoWayEdges({ bottom: v as EdgeCondition })}
                />
                <SelectField
                  label="Borde superior"
                  value={request.two_way_edges.top}
                  options={{ continuo: 'Continuo', simple: 'Simple' }}
                  onChange={(v) => setTwoWayEdges({ top: v as EdgeCondition })}
                />
                <SelectField
                  label="Borde izquierdo"
                  value={request.two_way_edges.left}
                  options={{ continuo: 'Continuo', simple: 'Simple' }}
                  onChange={(v) => setTwoWayEdges({ left: v as EdgeCondition })}
                />
                <SelectField
                  label="Borde derecho"
                  value={request.two_way_edges.right}
                  options={{ continuo: 'Continuo', simple: 'Simple' }}
                  onChange={(v) => setTwoWayEdges({ right: v as EdgeCondition })}
                />
              </>
            )}
          </Section>

          <Section title="Cargas">
            <SelectField
              label="Uso (E.020)"
              value={request.auto_loads.use}
              options={USE_LABELS as Record<string, string>}
              onChange={(v) => setAutoLoads({ use: v as LiveLoadUse })}
            />
            <Row>
              <NumberField
                label="Acabados"
                value={request.auto_loads.acabados_kgf_m2}
                onChange={(v) => setAutoLoads({ acabados_kgf_m2: v })}
              />
              <NumberField
                label="Tabique"
                value={request.auto_loads.tabiqueria_kgf_m2}
                onChange={(v) => setAutoLoads({ tabiqueria_kgf_m2: v })}
              />
            </Row>
            <NumberField
              label="Cielo raso"
              value={request.auto_loads.cielo_raso_kgf_m2}
              onChange={(v) => setAutoLoads({ cielo_raso_kgf_m2: v })}
            />
          </Section>
        </div>

        {/* RESULTS */}
        <div className="overflow-y-auto p-6 scrollbar-thin">
          {data ? (
            <>
              <Card title="Cargas calculadas">
                <Row cols={2}>
                  <div>
                    <RowKV k="Peso propio losa" v={data.breakdown.q_slab_self} unit="kgf/m²" />
                    <RowKV k="Acabados" v={data.breakdown.q_finishes} unit="kgf/m²" />
                    <RowKV k="Tabiquería" v={data.breakdown.q_partitions} unit="kgf/m²" />
                    <RowKV k="Cielo raso" v={data.breakdown.q_ceiling} unit="kgf/m²" />
                    <Divider />
                    <RowKV k="q_D total" v={data.breakdown.q_D_surface} unit="kgf/m²" bold />
                    <RowKV k="q_L (E.020)" v={data.breakdown.q_L_surface} unit="kgf/m²" bold />
                  </div>
                  <div>
                    <RowKV k="w_u" v={data.breakdown.w_u_surface} unit="kgf/m²" bold accent />
                    <Divider />
                    <RowKV k="Comportamiento" v={data.behavior === 'one_way' ? 'Una dirección' : 'Dos direcciones'} />
                    <RowKV k="Ly/Lx" v={data.Ly_over_Lx} />
                    <RowKV k="Combo" v={data.governing_combo} />
                  </div>
                </Row>
              </Card>

              {data.behavior === 'one_way' && data.one_way && (
                <Card title="Momentos y cortantes (por vigueta o franja 1m)">
                  <div className="grid grid-cols-2 gap-3 text-[12px] sm:grid-cols-4">
                    <ForceItem label="Mu(-) izq" value={data.one_way.Mu_neg_left / 100} unit="kgf·m" />
                    <ForceItem label="Mu(+) cnt" value={data.one_way.Mu_pos_mid / 100} unit="kgf·m" />
                    <ForceItem label="Mu(-) der" value={data.one_way.Mu_neg_right / 100} unit="kgf·m" />
                    <ForceItem label="Vu" value={data.one_way.Vu} unit="kgf" />
                  </div>
                </Card>
              )}

              {data.behavior === 'two_way' && data.two_way && (
                <Card title={`Momentos (Method 3 — caso ${data.two_way.case_number}, m=${data.two_way.m.toFixed(2)})`}>
                  <div className="grid grid-cols-2 gap-3 text-[12px] sm:grid-cols-4">
                    <ForceItem label="Mu_a (-) corto" value={data.two_way.Mu_a_neg / 100} unit="kgf·m/m" />
                    <ForceItem label="Mu_a (+) corto" value={data.two_way.Mu_a_pos / 100} unit="kgf·m/m" />
                    <ForceItem label="Mu_b (-) largo" value={data.two_way.Mu_b_neg / 100} unit="kgf·m/m" />
                    <ForceItem label="Mu_b (+) largo" value={data.two_way.Mu_b_pos / 100} unit="kgf·m/m" />
                  </div>
                </Card>
              )}

              <WarningsCard items={data.warnings} />
            </>
          ) : (
            <div
              className="flex items-center justify-center py-20"
              style={{ color: 'var(--text-muted)' }}
            >
              <Loader2 size={20} className="mr-2 animate-spin" /> Calculando…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
